import { cloneDeep, isEqual } from "lodash";

/**
 * Mixin for managing attendance data in group session views.
 *
 * Provides reactive attendance tracking with change detection.
 *
 * Requirements:
 * - Consuming component must have `participants` available
 *   (from Vuex state: state.groupSessions.participants.data)
 */
export default {
  data() {
    return {
      attendanceData: {},
      originalAttendanceData: {},
      externalAttendance: 0,
      originalExternalAttendance: 0,
    };
  },
  computed: {
    hasUnsavedChanges() {
      const participantChanges = !isEqual(
        this.attendanceData,
        this.originalAttendanceData
      );
      const externalChanged =
        this.externalAttendance !== this.originalExternalAttendance;
      return participantChanges || externalChanged;
    },
    unsavedCount() {
      let count = 0;
      Object.keys(this.attendanceData).forEach((userId) => {
        if (
          !isEqual(
            this.attendanceData[userId],
            this.originalAttendanceData[userId]
          )
        ) {
          count++;
        }
      });
      return count;
    },
    changedUserIds() {
      return Object.keys(this.attendanceData).filter(
        (userId) =>
          !isEqual(
            this.attendanceData[userId],
            this.originalAttendanceData[userId]
          )
      );
    },
  },
  watch: {
    participants: {
      handler(val) {
        this.initAttendanceData(val);
      },
      immediate: true,
    },
  },
  methods: {
    initAttendanceData(participants) {
      const data = {};
      (participants || []).forEach((p) => {
        data[p.user_id] = {
          user_id: p.user_id,
          attendance_status: p.attendance_status || "SCHEDULED",
          attendance_notes: p.attendance_notes || "",
          is_fee_required: p.is_fee_required !== false,
        };
      });
      this.attendanceData = data;
      this.originalAttendanceData = cloneDeep(data);
    },
    setAttendance(userId, status) {
      if (this.attendanceData[userId]) {
        this.$set(this.attendanceData[userId], "attendance_status", status);
      }
    },
    setFeeRequired(userId, value) {
      if (this.attendanceData[userId]) {
        this.$set(this.attendanceData[userId], "is_fee_required", value);
      }
    },
    setAttendanceNotes(userId, value) {
      if (this.attendanceData[userId]) {
        this.$set(this.attendanceData[userId], "attendance_notes", value);
      }
    },
    getAttendance(userId) {
      return this.attendanceData[userId]?.attendance_status || "SCHEDULED";
    },
    getFeeRequired(userId) {
      return this.attendanceData[userId]?.is_fee_required !== false;
    },
    getAttendanceNotes(userId) {
      return this.attendanceData[userId]?.attendance_notes || "";
    },
    markAllPresent() {
      Object.keys(this.attendanceData).forEach((userId) => {
        this.$set(this.attendanceData[userId], "attendance_status", "PRESENT");
      });
    },
    markAllAbsent() {
      Object.keys(this.attendanceData).forEach((userId) => {
        this.$set(this.attendanceData[userId], "attendance_status", "ABSENT");
      });
    },
    isRowChanged(userId) {
      return this.changedUserIds.includes(String(userId));
    },
    initExternalAttendance(value) {
      const parsed = parseInt(value, 10);
      const normalized = Number.isFinite(parsed) ? parsed : 0;
      this.externalAttendance = normalized;
      this.originalExternalAttendance = normalized;
    },
    resetAttendanceData() {
      this.originalAttendanceData = cloneDeep(this.attendanceData);
      this.originalExternalAttendance = this.externalAttendance;
    },
  },
};
